import * as RadixPopover from '@radix-ui/react-popover';
import { KeyboardEvent, PointerEvent, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { coreTokens } from '../../../core';
import { restrictedZIndexes } from '../../../utils';
import { useText } from '../../../utils/hooks/use_text';
import { Button } from '../../button';
import { InputSwitch } from '../../form';
import { Icon } from '../../icon';
import { VisuallyHidden } from '../../visually_hidden';
import { theme } from './table_column_menu.theme';
import type { TableColumnPreferences } from './use_table_column_preferences';

/**
 * ## @visiba/cellula-table-column-menu
 *
 * CellulaTableColumnMenu
 *
 * Renders a self-contained trigger button + menu for toggling column
 * visibility and reordering columns, backed by `useTableColumnPreferences`.
 * Drop it anywhere - e.g. in a `Table`'s `header` slot, alongside other
 * filters.
 */
export interface TableColumnMenuProps<TRow> {
	/**
	 * The state and actions from `useTableColumnPreferences`.
	 */
	preferences: TableColumnPreferences<TRow>;

	/**
	 * Label shown on the trigger button.
	 * @default Localized 'Columns'
	 */
	triggerLabel?: string;
}

const elements = {
	content: styled(RadixPopover.Content)`
		z-index: ${restrictedZIndexes.high};
		box-sizing: border-box;
		border: 1px solid ${theme.getValue('contentBorderColor')};
		border-radius: ${theme.getValue('contentBorderRadius')};
		background: ${theme.getValue('contentBackground')};
		box-shadow: ${theme.getValue('contentBoxShadow')};
		padding: ${coreTokens.spacing.x1} 0;
		display: flex;
		flex-direction: column;
		min-width: 260px;
		max-width: 320px;
	`,
	list: styled.ul`
		list-style: none;
		margin: 0;
		padding: 0 ${coreTokens.spacing.x0_5};
		max-height: 320px;
		overflow-y: auto;
	`,
	item: styled.li<{ $dragging: boolean }>`
		box-sizing: border-box;
		display: flex;
		align-items: center;
		gap: ${coreTokens.spacing.x0_5};
		padding: ${coreTokens.spacing.x1} ${coreTokens.spacing.x1};
		border-radius: ${coreTokens.borderRadius.xSmall};
		font: ${theme.getValue('itemFont')};
		color: ${theme.getValue('itemFontColor')};
		opacity: ${(props) => (props.$dragging ? 0.4 : 1)};

		@media (hover: hover) {
			&:hover {
				background: ${theme.getValue('itemHoverBackground')};
			}
		}
	`,
	grip: styled.button`
		all: unset;
		box-sizing: border-box;
		display: flex;
		flex-shrink: 0;
		color: ${theme.getValue('gripIconColor')};
		cursor: grab;
		touch-action: none;
		border-radius: ${coreTokens.borderRadius.xSmall};

		&:active {
			cursor: grabbing;
		}
		&:focus-visible {
			outline: 2px solid ${theme.getValue('focusRingColor')};
			outline-offset: 2px;
		}
	`,
	itemLabel: styled.span`
		flex: 1;
		min-width: 0;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	`,
	// Floating clone that tracks the pointer while dragging - the dimmed row
	// left behind in the list (see `item`'s `$dragging` prop) acts as the
	// drop-position placeholder, positioned absolutely against `content`
	// (which Radix already renders with `position: fixed`, making it this
	// element's containing block). Only handles position/elevation - the
	// row's own look (padding, font, layout) comes from reusing `item` as
	// its immediate child, so the ghost is a true clone of the dragged row.
	dragGhost: styled.div`
		position: absolute;
		top: 0;
		left: 0;
		z-index: 1;
		box-sizing: border-box;
		border-radius: ${coreTokens.borderRadius.xSmall};
		background: ${theme.getValue('contentBackground')};
		box-shadow: ${theme.getValue('contentBoxShadow')};
		pointer-events: none;
		will-change: transform;
	`,
};

export function TableColumnMenu<TRow>({ preferences, triggerLabel }: TableColumnMenuProps<TRow>): JSX.Element {
	const [open, setOpen] = useState(false);
	const [announcement, setAnnouncement] = useState('');
	const [draggingKey, setDraggingKey] = useState<string | null>(null);
	const [dragGhost, setDragGhost] = useState<{
		label: string;
		visible: boolean;
		width: number;
		height: number;
		left: number;
		top: number;
	} | null>(null);
	const text = useText();

	const contentRef = useRef<HTMLDivElement | null>(null);
	const listElementRef = useRef<HTMLUListElement | null>(null);
	const dragGhostElementRef = useRef<HTMLDivElement | null>(null);
	// `preferences` is a fresh object every render - keep the latest version
	// in a ref so the document-level drag listeners (attached once per drag)
	// always call the current `reorder` function.
	const preferencesRef = useRef(preferences);
	preferencesRef.current = preferences;
	const dragStateRef = useRef<{ index: number; pointerId: number; offsetX: number; offsetY: number } | null>(null);
	// Rects of every row, captured immediately before a reorder - compared
	// against their post-render rects in the layout effect below to animate
	// the slide (FLIP technique). Populated by `reorderWithFlip`, for both
	// the pointer-drag and keyboard reorder paths.
	const previousRectsRef = useRef<Map<string, DOMRect> | null>(null);
	// Last known *settled* (non-animating) rect per row, and which rows are
	// currently mid-slide. While a row is animating, its live
	// `getBoundingClientRect()` reflects an in-between position, which would
	// make the drop-target hit-test in `handleGripPointerMove` flicker as
	// that rect keeps moving under a stationary pointer - so hit-testing
	// uses the settled rect for any row that's still animating instead.
	// Mirrors SortableJS's own `target.animated ? target.toRect : targetRect`.
	const settledRectsRef = useRef<Map<string, DOMRect>>(new Map());
	const animatingKeysRef = useRef<Set<string>>(new Set());

	function reorderWithFlip(fromIndex: number, toIndex: number): void {
		const list = listElementRef.current;
		if (list) {
			const rects = new Map<string, DOMRect>();
			for (const child of Array.from(list.children) as HTMLElement[]) {
				const key = child.dataset.key;
				if (key) rects.set(key, child.getBoundingClientRect());
			}
			previousRectsRef.current = rects;
		}
		preferencesRef.current.reorder(fromIndex, toIndex);
	}

	// Animates every row that changed position after a reorder, sliding it
	// from where it used to be to where it landed instead of letting it jump
	// there instantly. Runs for both the pointer-drag and keyboard reorder
	// paths, since both go through `reorderWithFlip`.
	// biome-ignore lint/correctness/useExhaustiveDependencies: `preferences.items` isn't read directly, but the effect must re-run whenever the row order changes to animate the new layout.
	useLayoutEffect(() => {
		const list = listElementRef.current;
		const previousRects = previousRectsRef.current;
		previousRectsRef.current = null;
		if (!list) return;

		for (const child of Array.from(list.children) as HTMLElement[]) {
			const key = child.dataset.key;
			if (!key) continue;

			// The DOM has already re-rendered in the new order by this point,
			// with no transform applied yet - this rect is the settled
			// destination, cache it regardless of whether this row moved.
			const toRect = child.getBoundingClientRect();
			settledRectsRef.current.set(key, toRect);

			// The dragged row is already tracked by the floating ghost - it
			// should snap to its placeholder slot instantly, not slide there.
			if (!previousRects || key === draggingKey) continue;

			const previousRect = previousRects.get(key);
			if (!previousRect) continue;

			const deltaY = previousRect.top - toRect.top;
			if (!deltaY) continue;

			child.style.transition = 'none';
			child.style.transform = `translateY(${deltaY}px)`;
			void child.offsetWidth; // force a synchronous reflow before animating to the identity transform
			child.style.transition = 'transform 150ms ease';
			child.style.transform = '';

			animatingKeysRef.current.add(key);
			window.setTimeout(() => animatingKeysRef.current.delete(key), 150);
		}
	}, [preferences.items, draggingKey]);

	function handleGripPointerDown(event: PointerEvent<HTMLButtonElement>, index: number): void {
		if (event.pointerType === 'mouse' && event.button !== 0) return;

		const gripElement = event.currentTarget;
		const itemElement = gripElement.closest('li');
		const listElement = listElementRef.current;
		const contentElement = contentRef.current;
		if (!itemElement || !listElement || !contentElement) return;

		gripElement.setPointerCapture(event.pointerId);
		const itemRect = itemElement.getBoundingClientRect();
		const contentRect = contentElement.getBoundingClientRect();
		const pointerId = event.pointerId;
		dragStateRef.current = {
			index,
			pointerId,
			offsetX: event.clientX - itemRect.left,
			offsetY: event.clientY - itemRect.top,
		};
		setDraggingKey(preferences.items[index].key);
		setDragGhost({
			label: preferences.items[index].label,
			visible: preferences.items[index].visible,
			width: itemRect.width,
			height: itemRect.height,
			left: itemRect.left - contentRect.left,
			top: itemRect.top - contentRect.top,
		});

		// Moves the dragged column as soon as the pointer crosses the midpoint
		// of a neighbouring item, mirroring how most native reorderable lists
		// (e.g. Finder, Slack's sidebar) behave.
		function handleDocumentPointerMove(moveEvent: globalThis.PointerEvent): void {
			const dragState = dragStateRef.current;
			if (!dragState || !listElement || moveEvent.pointerId !== pointerId) return;

			if (dragGhostElementRef.current) {
				const currentContentRect = contentElement?.getBoundingClientRect();
				if (currentContentRect) {
					const left = moveEvent.clientX - currentContentRect.left - dragState.offsetX;
					const top = moveEvent.clientY - currentContentRect.top - dragState.offsetY;
					dragGhostElementRef.current.style.transform = `translate(${left}px, ${top}px)`;
				}
			}

			const rowElements = Array.from(listElement.children) as HTMLElement[];
			const pointerY = moveEvent.clientY;
			let targetIndex = rowElements.length - 1;
			for (const [rowIndex, rowElement] of rowElements.entries()) {
				const rowKey = rowElement.dataset.key;
				const settledRect = rowKey ? settledRectsRef.current.get(rowKey) : undefined;
				const rect = rowKey && animatingKeysRef.current.has(rowKey) && settledRect ? settledRect : rowElement.getBoundingClientRect();
				if (pointerY < rect.top + rect.height / 2) {
					targetIndex = rowIndex;
					break;
				}
			}

			if (targetIndex !== dragState.index) {
				reorderWithFlip(dragState.index, targetIndex);
				dragState.index = targetIndex;
			}
		}

		// Always ends the drag on release, even if the pointer ends up
		// outside the grip, or the popover closes mid-drag and removes it
		// from the DOM (in which case the grip's own pointerup/pointercancel
		// would never fire) - SortableJS binds its drop handlers to
		// `document` for the same reason, rather than the drag handle.
		function endDrag(dropEvent: globalThis.PointerEvent): void {
			if (dropEvent.pointerId !== pointerId) return;
			document.removeEventListener('pointermove', handleDocumentPointerMove);
			document.removeEventListener('pointerup', endDrag);
			document.removeEventListener('pointercancel', endDrag);

			if (gripElement.isConnected && gripElement.hasPointerCapture(pointerId)) {
				gripElement.releasePointerCapture(pointerId);
			}
			if (dragStateRef.current?.pointerId === pointerId) dragStateRef.current = null;
			setDraggingKey(null);
			setDragGhost(null);
		}

		document.addEventListener('pointermove', handleDocumentPointerMove);
		document.addEventListener('pointerup', endDrag);
		document.addEventListener('pointercancel', endDrag);
	}

	// Keyboard equivalent of dragging: moves the column immediately (no
	// separate pick-up/drop step) since the grip button already only performs
	// this one action. Arrow keys are the only supported shortcut - matches
	// how ordinary lists (e.g. macOS Finder) reorder selections.
	function handleGripKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number, label: string): void {
		if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;

		// Prevent the page from scrolling under the arrow key press - this
		// must happen before the boundary check below, since the key should
		// never cause a scroll while the grip has focus, whether or not the
		// column can actually move any further in that direction.
		event.preventDefault();

		const toIndex = event.key === 'ArrowUp' ? index - 1 : index + 1;
		if (toIndex < 0 || toIndex >= preferences.items.length) return;

		reorderWithFlip(index, toIndex);
		setAnnouncement(
			text('Component.TableColumnMenu.MovedAnnouncement', {
				column: label,
				position: String(toIndex + 1),
				total: String(preferences.items.length),
			}),
		);
	}

	return (
		<RadixPopover.Root open={open} onOpenChange={setOpen}>
			<RadixPopover.Trigger asChild>
				<Button variant='ghost' size='small' icon='cog'>
					{triggerLabel ?? text('Component.TableColumnMenu.Trigger')}
				</Button>
			</RadixPopover.Trigger>
			<RadixPopover.Portal>
				<elements.content ref={contentRef} align='end' sideOffset={4} collisionPadding={8}>
					<elements.list ref={listElementRef}>
						{preferences.items.map((item, index) => (
							<elements.item key={item.key} data-key={item.key} $dragging={item.key === draggingKey}>
								<InputSwitch
									checked={item.visible}
									disabled={!item.canHide}
									aria-label={text('Component.TableColumnMenu.ToggleColumn', { column: item.label })}
									onCheckedChange={() => preferences.toggleVisibility(item.key)}
								/>
								<elements.itemLabel>{item.label}</elements.itemLabel>
								<elements.grip
									type='button'
									aria-label={text('Component.TableColumnMenu.ReorderColumn', { column: item.label })}
									onKeyDown={(event) => handleGripKeyDown(event, index, item.label)}
									onPointerDown={(event) => handleGripPointerDown(event, index)}
								>
									<Icon type='menu' size='small' />
								</elements.grip>
							</elements.item>
						))}
					</elements.list>
					{dragGhost && (
						<elements.dragGhost
							ref={dragGhostElementRef}
							aria-hidden
							style={{
								width: dragGhost.width,
								height: dragGhost.height,
								transform: `translate(${dragGhost.left}px, ${dragGhost.top}px)`,
							}}
						>
							<elements.item as='div' $dragging={false} style={{ width: '100%', height: '100%' }}>
								<InputSwitch checked={dragGhost.visible} tabIndex={-1} />
								<elements.itemLabel>{dragGhost.label}</elements.itemLabel>
								<elements.grip type='button' tabIndex={-1}>
									<Icon type='menu' size='small' />
								</elements.grip>
							</elements.item>
						</elements.dragGhost>
					)}
					<VisuallyHidden role='status'>{announcement}</VisuallyHidden>
				</elements.content>
			</RadixPopover.Portal>
		</RadixPopover.Root>
	);
}

TableColumnMenu.displayName = 'CellulaTableColumnMenu';
